import type { PathStep } from '~/pages/backoffice/admin/sessions/path-step'
import { useTranslation } from 'react-i18next'
import { Input } from '~/components/ui/input'
import { defaultMinScore, minScoreFromPercent, withPrerequisite } from '~/pages/backoffice/admin/sessions/session-path-form'

type Option = { moduleId: string, title: string, previous: boolean, masteryScore?: number | null }

type IProps = {
  step: PathStep
  /** Prérequis proposables, le précédent en premier (`prerequisiteOptions`). */
  options: Option[]
  onChange: (next: Pick<PathStep, 'prerequisiteModuleId' | 'prerequisiteMinScore'>) => void
}

/**
 * Condition d'ouverture d'une étape (#80) : un module à terminer avant, et en
 * option un score minimum à y obtenir. Le score se saisit en pourcentage et se
 * stocke sur [0..1], comme le seuil de réussite qui le préremplit.
 */
function PrerequisiteFields({ step, options, onChange }: IProps) {
  const { t } = useTranslation()
  const selected = options.find(o => o.moduleId === step.prerequisiteModuleId)
  const minScore = step.prerequisiteMinScore
  const id = `prerequisite-${step.moduleId}`

  return (
    <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-1.5 pl-9 text-xs text-muted-foreground">
      <label htmlFor={id}>{t('sessions.path.prerequisite')}</label>
      <select
        id={id}
        value={step.prerequisiteModuleId ?? ''}
        onChange={event => onChange(withPrerequisite(minScore, options.find(o => o.moduleId === event.target.value)))}
        className="h-7 max-w-[16rem] truncate rounded-md border border-input bg-transparent px-2 text-xs text-foreground"
      >
        <option value="">{t('sessions.path.prerequisite.none')}</option>
        {options.map(o => (
          <option key={o.moduleId} value={o.moduleId}>
            {o.previous ? t('sessions.path.prerequisite.previous', { title: o.title }) : o.title}
          </option>
        ))}
      </select>

      {selected && (
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={minScore != null}
            onChange={event => onChange({
              prerequisiteModuleId: selected.moduleId,
              prerequisiteMinScore: event.target.checked ? defaultMinScore(selected.masteryScore) : null,
            })}
          />
          {t('sessions.path.minScore')}
        </label>
      )}

      {selected && minScore != null && (
        <span className="flex items-center gap-1">
          <Input
            type="number"
            min={0}
            max={100}
            step={1}
            className="h-7 w-16 text-xs"
            aria-label={t('sessions.path.minScore.label', { title: selected.title })}
            value={Math.round(minScore * 100)}
            onChange={event => onChange({
              prerequisiteModuleId: selected.moduleId,
              prerequisiteMinScore: minScoreFromPercent(event.target.value),
            })}
          />
          %
          {selected.masteryScore != null && (
            <span>{t('sessions.path.minScore.mastery', { score: Math.round(selected.masteryScore * 100) })}</span>
          )}
        </span>
      )}

      {/* Un module sans seuil déclaré ne remonte souvent aucun score : exiger un
          score fermerait alors le suivant pour tout le monde — l'API traite
          « pas de score » comme « pas atteint ». */}
      {selected && minScore != null && selected.masteryScore == null && (
        <p className="w-full text-warning-text">{t('sessions.path.minScore.noMastery')}</p>
      )}
    </div>
  )
}

export default PrerequisiteFields
